/**
 * hexPipeline — bioluminescent hexagonal cells, audio-reactive.
 *
 * Port of "Bioluminescent Hex Cells — Audio Reactive" from Shadertoy.
 * Each hex cell has:
 *   - a body (the metal/dark backdrop)
 *   - an inner ring (driven by mid audio)
 *   - a glowing core (driven by bass + mid)
 *   - optional sparkle flash (driven by high audio, on selected cells)
 *
 * The cells are arranged via the hexCell() function which returns
 * (cellId.xy, edgeDistance, centerDistance). The edgeDistance + centerDistance
 * values are used to draw the body, ring, and core shapes.
 *
 * Audio is read from engine.beatAudio (mic) or synthesized via
 * computeSyntheticBands(t). The original shader uses three "spectrum
 * bands" (bass/mid/high) each averaged over 8 texture samples — we
 * reproduce that with our 9-band sampler.
 *
 * Attribution: shader logic by the original Shadertoy author (MIT).
 * NSPP integration + audio abstraction mine.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface HexPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  /** 9-band audio data; we use the first 9 values to map to bass/mid/high. */
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// --- Hash, value noise, FBM (Shadertoy original) ---
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float result = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 4; i++) {
    result += amplitude * valueNoise(p);
    p = p * 2.03 + 7.1;
    amplitude *= 0.5;
  }
  return result;
}

// --- Hex cell SDF returning (id.xy, edgeDistance, centerDistance) ---
// Exact port of the Shadertoy hexCell function.
vec4 hexCell(vec2 p) {
  const float SQRT3 = 1.7320508;
  vec2 q = vec2(p.x * 1.1547, p.y + p.x * 0.57735);
  vec2 base = floor(q);
  vec2 local = fract(q);
  float parity = mod(base.x + base.y, 3.0);
  float a = step(1.0, parity);
  float b = step(2.0, parity);
  vec2 diagonal = step(local.xy, local.yx);
  float edge = dot(
    diagonal,
    1.0 - local.yx
    + a * (local.x + local.y - 1.0)
    + b * (local.yx - 2.0 * local.xy)
  );
  vec2 id = base + a - b * diagonal;
  vec2 centerSpace = vec2(
    q.x + floor(0.5 + p.y / 1.5),
    4.0 * p.y / 3.0
  ) * 0.5 + 0.5;
  float center = length(
    (fract(centerSpace) - 0.5)
    * vec2(1.0, SQRT3 * 0.5)
  );
  return vec4(id, edge, center);
}

void main() {
  vec2 uv = vUV;
  vec2 p = (2.0 * gl_FragCoord.xy - uResolution)
         / uResolution.y;

  // Audio bands — average our 3 sub-bands per major band.
  // bass.x/.y/.z = sub-bass / kick / hi-bass
  // mid.x/.y/.z = low-mid / mid / hi-mid
  // treble.x/.y/.z = low-treble / mid-treble / air
  // For "bass" use sub-bass + kick avg, "mid" use mid-band avg, "high"
  // use treble avg.
  float bass = (uBass.x + uBass.y) * 0.5;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;

  // Gain + compression matching original.
  bass = pow(clamp(bass * 2.7, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 3.5, 0.0, 1.0), 0.75);
  high = pow(clamp(high * 5.0, 0.0, 1.0), 0.85);

  float totalAudio = bass * 0.55 + mid * 0.30 + high * 0.15;

  // Bass warps space (radial barrel distortion).
  p *= 1.0 + 0.12 * dot(p, p);
  p *= 1.0 - bass * 0.055;

  // Audio-driven radial ripple.
  float radius = length(p);
  p += normalize(p + 0.0001)
     * sin(radius * 12.0 - uTime * 3.0)
     * bass * 0.025;

  // Slow drift across the field.
  vec2 drift = vec2(
    sin(uTime * 0.18),
    cos(uTime * 0.14)
  ) * (0.35 + bass * 0.15);

  vec4 h = hexCell(p * (7.2 - bass * 0.4) + drift);
  vec2 cellId = h.xy;

  float cellSeed = hash21(cellId);
  float field = fbm(
    cellId * 0.42
    + vec2(uTime * 0.08, -uTime * 0.05)
  );

  // Each cell has its own phase.
  float wave = sin(
    uTime * (1.2 + mid * 2.0) + cellSeed * 10.0
  );

  float cellAudio = totalAudio * (0.65 + 0.70 * cellSeed);

  float pulse = smoothstep(
    0.40, 0.68,
    field + 0.16 * wave + cellAudio * 0.55
  );

  // --- Geometry ---
  float body = smoothstep(0.075, 0.11, h.z);
  float ringRadius = 0.17 + bass * 0.025;
  float innerRing =
    smoothstep(ringRadius + 0.025, ringRadius, h.w)
    * smoothstep(ringRadius - 0.085, ringRadius - 0.065, h.w);

  // --- Color palette ---
  vec3 darkMetal = vec3(0.012, 0.025, 0.050);
  vec3 cyan = vec3(0.02, 0.75, 0.96);
  vec3 violet = vec3(0.42, 0.08, 0.95);
  vec3 hotPink = vec3(1.00, 0.05, 0.42);

  vec3 glowColor = mix(cyan, violet, cellSeed);
  glowColor = mix(glowColor, hotPink, high * 0.45);

  vec3 col = darkMetal;
  col += vec3(0.025, 0.06, 0.10) * body;

  // Mid audio drives the inner rings.
  col += glowColor
    * innerRing
    * (0.25 + 1.3 * pulse + mid * 2.0);

  // Bass + mid drive the core.
  float coreRadius = 0.105 + bass * 0.025;
  float core = smoothstep(coreRadius, 0.015, h.w);
  col += glowColor
    * core
    * (0.25 + pulse * 1.5 + bass * 2.2 + mid);

  // High audio drives sparkles on selected cells.
  float sparkleCell = step(0.82, cellSeed);
  float sparkle = sparkleCell
    * high
    * pow(max(0.0, sin(uTime * 15.0 + cellSeed * 50.0)), 8.0);
  float sparkleShape = smoothstep(0.14, 0.0, h.w);
  col += vec3(0.75, 0.9, 1.0)
    * sparkle
    * sparkleShape
    * 3.0;

  // Plate separation darkens edges.
  col *= smoothstep(0.015, 0.045, h.z);

  // Global glow tied to field + audio.
  col += glowColor
    * body
    * (0.03 + field * 0.035 + totalAudio * 0.11);

  // Vignette.
  float vignette = pow(
    16.0 * uv.x * (1.0 - uv.x)
    * uv.y * (1.0 - uv.y),
    0.16
  );
  col *= vignette;

  // Tone mapping + gamma.
  col = col / (1.0 + col);
  col = pow(col, vec3(0.85));

  outColor = vec4(col, 1.0);
}
`;

/**
 * Synthesize 9 frequency-band values. Same shape as the Beat Nebula
 * fallback but tuned for "music" feel (slower, more harmonic).
 */
export function computeHexBands(t: number, out: Float32Array): void {
  // Bass — slow kick-like pulses
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.8);                  // sub-bass
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.6));       // kick
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.4);                  // hi-bass
  // Mid — vocal/instrument range
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.1 + 1.0);           // low-mid
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.7 + 2.3);           // mid
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.5 + 3.1);           // hi-mid
  // High — cymbals, sibilance
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);           // low-treble
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);           // mid-treble
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);           // air
}

/**
 * Map a real AudioContext frequency array to 9 bands. We use a
 * different grouping here than the Beat Nebula: sub-bass bins 0-8,
 * bass bins 8-32, low-mid 32-128, mid 128-256, hi-mid 256-512,
 * lo-treble 512-768, mid-treble 768-896, air 896-1023.
 */
export function computeHexRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008],    // sub-bass
    [0.008, 0.030],   // kick
    [0.030, 0.060],   // hi-bass
    [0.060, 0.150],   // low-mid
    [0.150, 0.260],   // mid
    [0.260, 0.420],   // hi-mid
    [0.420, 0.620],   // low-treble
    [0.620, 0.800],   // mid-treble
    [0.800, 0.980],   // air
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createHexPipeline(gl: WebGL2RenderingContext): HexPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawHex(
  gl: WebGL2RenderingContext,
  pipe: HexPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
